import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useRealtimeEvent,
  REALTIME_EVENTS,
  type OpportunityUpdatedEvent,
} from '@/hooks/useRealtimeEvent';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Download,
  ExternalLink,
  Loader2,
  Pencil,
  Send,
  Upload,
  X as XIcon,
} from 'lucide-react';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dropzone } from '@/components/ui/Dropzone';
import { Banner } from '@/components/ui/Banner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Input, Label, Select, Textarea } from '@/components/ui/Input';
import { PipelineStageBadge, Badge } from '@/components/ui/StatusBadge';
import { Tooltip } from '@/components/ui/Tooltip';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { useAuthStore } from '@/stores/auth';
import {
  opportunitiesApi,
  LOSS_REASON_LABELS,
  PIPELINE_STAGES,
  STAGE_LABELS,
  type Checklist,
  type LossReason,
  type OpportunityDetail,
  type OpportunityStage,
  type Quote,
  type ScoreBreakdown,
} from '@/services/opportunities.api';
import { CloseLostModal, computeDefaultReattackDate } from '@/components/CloseLostModal';
import { EstimatedSigningDateModal } from '@/components/EstimatedSigningDateModal';
import { InquiriesSection } from '@/components/InquiriesSection';
import { TimelineSection } from '@/components/TimelineSection';
import { documentsApi } from '@/services/documents.api';
import {
  configApi,
  type InsuranceCompany as Company,
  type ContractType,
} from '@/services/config.api';
import { usersApi, type TeamMember } from '@/services/users.api';
import { apporteursApi, apporteurDisplayName } from '@/services/apporteurs.api';
import { cn } from '@/lib/utils';

interface Props {
  opportunityId: string;
  onBack: () => void;
  hideTimeline?: boolean;
  onOpportunityChanged?: () => void;
}

/**
 * Vue détaillée d'une opportunité - embarquée dans la fiche prospect.
 *
 * Structure condensée à la Stripe :
 *  - Carte « Opportunité » qui regroupe résumé (contrat, commercial),
 *    données financières (primes, taux, commission, CA pondéré) et date
 *    d'effet, avec un bouton « Modifier » pour éditer primes, taux,
 *    commercial et date d'effet en une seule soumission.
 *  - Le prospect et le score ne sont pas dans cette carte : le prospect
 *    est dans l'en-tête de la page hôte, le score vit dans la sidebar
 *    via `<OpportunityScoreCard>`.
 */
export function OpportunityDetailView({
  opportunityId,
  onBack,
  hideTimeline = false,
  onOpportunityChanged,
}: Props) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const navigate = useNavigate();
  const [opp, setOpp] = useState<OpportunityDetail | null>(null);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  // `loading` = chargement initial (pas encore de donnée). On bascule sur le
  // Skeleton seulement dans ce cas. Les rechargements après action (changement
  // d'étape, sauvegarde, etc.) utilisent `refreshing` pour afficher un petit
  // indicateur non intrusif sans vider l'écran.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closeLostOpen, setCloseLostOpen] = useState(false);
  // ===== State lifé pour `CloseLostModal` (item #5 dette pipeline V2) =====
  // La saisie survit aux fermetures/réouvertures de la modale dans la même
  // session. `reattackOn` est initialisé à `''` puis posé sur la default
  // computed via `useEffect` quand `opp` devient disponible (on ne peut pas
  // le calculer avant car on a besoin de `opp.effectiveDate`).
  const [closeLostReason, setCloseLostReason] = useState<LossReason>('price');
  const [closeLostComment, setCloseLostComment] = useState('');
  const [closeLostReattackEnabled, setCloseLostReattackEnabled] = useState(false);
  const [closeLostReattackOn, setCloseLostReattackOn] = useState<string>('');
  // `reattackOnInitialized` empêche le useEffect ci-dessous d'écraser une
  // saisie utilisateur en cas de refetch d'`opp`. La default n'est posée
  // qu'une seule fois (premier render avec opp non null).
  const reattackOnInitialized = useRef(false);
  useEffect(() => {
    if (!opp || reattackOnInitialized.current) return;
    setCloseLostReattackOn(computeDefaultReattackDate(opp.effectiveDate));
    reattackOnInitialized.current = true;
  }, [opp]);

  const load = useCallback(
    async (opts: { initial?: boolean } = {}) => {
      if (!opportunityId) return;
      if (opts.initial) setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const [o, c] = await Promise.all([
          opportunitiesApi.get(token, opportunityId),
          opportunitiesApi.checklist(token, opportunityId),
        ]);
        setOpp(o);
        setChecklist(c);
        onOpportunityChanged?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [opportunityId, token, onOpportunityChanged],
  );

  useEffect(() => {
    // Changement d'opp → remet `loading` à true pour afficher le skeleton
    // initial. Les rechargements d'une même opp passent par `refreshing`.
    setLoading(true);
    void load({ initial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);

  // Realtime : refresh automatique de la fiche opp à chaque mutation émise
  // par le backend (changeStage, addEvent, uploadDoc, quote-select, etc.)
  // côté n'importe quel acteur autorisé. Filtre strict par id pour ne pas
  // refetch sur les mutations des autres opps. OpportunityScoreCard a son
  // propre listener → le score se rafraîchit en parallèle.
  useRealtimeEvent<OpportunityUpdatedEvent>(
    REALTIME_EVENTS.OPPORTUNITY_UPDATED,
    useCallback(
      (ev) => {
        if (ev.id === opportunityId) void load();
      },
      [opportunityId, load],
    ),
  );

  useEffect(() => {
    void (async () => {
      try {
        const res = await configApi.listInsuranceCompanies(token, true);
        setCompanies(res);
      } catch {
        setCompanies([]);
      }
    })();
  }, [token]);

  async function changeStage(target: OpportunityStage, direction: 'forward' | 'backward') {
    // Garde métier côté client : l'étape "Devis émis" ne doit pas pouvoir
    // être atteinte sans qu'au moins un PDF de devis ait été chargé. Le
    // backend impose déjà cette règle implicitement (auto #3) mais on court-
    // circuite l'appel pour afficher un message explicite plutôt qu'une
    // erreur technique - et on évite un round-trip inutile.
    if (direction === 'forward' && target === 'quote_sent' && opp && opp.quotes.length === 0) {
      toast({
        tone: 'warning',
        title: 'Devis manquant',
        message:
          'Chargez au moins un devis PDF dans l\u2019opportunité avant de passer à « Devis émis ».',
      });
      return;
    }
    setTransitioning(true);
    try {
      const updated = await opportunitiesApi.changeStage(token, opportunityId, target);
      setOpp(updated);
      toast({
        tone: 'success',
        message:
          direction === 'forward'
            ? `Étape passée à « ${STAGE_LABELS[target]} ».`
            : `Retour à « ${STAGE_LABELS[target]} ».`,
      });
      await load();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Transition impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    } finally {
      setTransitioning(false);
    }
  }

  async function handleCloseLost(
    reason: LossReason,
    comment: string | undefined,
    reattackOn: string | null,
  ) {
    const updated = await opportunitiesApi.closeLost(
      token,
      opportunityId,
      reason,
      comment,
      reattackOn,
    );
    // `reattackTaskId` est posé par le backend uniquement si une tâche de
    // relance a effectivement été créée (cf flat extension du payload
    // closeLost — item #6 dette pipeline V2).
    const { reattackTaskId, ...oppPayload } = updated;
    setOpp(oppPayload);
    setCloseLostOpen(false);
    // Toast adapté : on signale la création de la tâche de relance pour
    // que l'utilisateur sache exactement ce qui a été planifié.
    // Item #6 : si une tâche a été créée, on expose un raccourci « Voir
    // mes tâches » qui ouvre la vue calendrier filtrée tâches sur le scope
    // « mine » (deep-link existant `/calendar?filter=tasks&scope=mine`).
    toast({
      tone: 'warning',
      title: 'Opportunité perdue',
      message: reattackOn
        ? `${LOSS_REASON_LABELS[reason]} · relance planifiée le ${new Date(
            `${reattackOn}T00:00:00Z`,
          ).toLocaleDateString('fr-FR')}`
        : LOSS_REASON_LABELS[reason],
      ...(reattackTaskId
        ? {
            action: {
              label: 'Voir mes tâches',
              onClick: () => navigate('/calendar?filter=tasks&scope=mine'),
            },
          }
        : {}),
    });
    void load();
  }

  async function handleReopen() {
    try {
      const updated = await opportunitiesApi.reopen(token, opportunityId);
      setOpp(updated);
      toast({ tone: 'success', message: 'Opportunité rouverte.' });
      void load();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Réouverture impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  // Skeleton uniquement au tout premier chargement (ou lors du switch d'opp).
  // Les rafraîchissements après action préservent l'écran pour éviter le flash
  // blanc gênant pointé par l'utilisateur.
  if (loading && !opp) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (error || !opp || !checklist) {
    return <Banner tone="danger">{error ?? 'Opportunité introuvable'}</Banner>;
  }

  const stageIdx = PIPELINE_STAGES.indexOf(opp.stage);
  const canAdvance = stageIdx >= 0 && stageIdx < PIPELINE_STAGES.length - 1;
  const isTerminal = opp.stage === 'lost' || opp.stage === 'no_follow_up';
  // Retour arrière : autorisé pour toutes les étapes non-terminales, y compris
  // depuis « Signé » → « Devis émis » (correction d'erreur de saisie). Le
  // backend gère les effets de bord : `signedAt` réinitialisé et statut
  // prospect recalculé (reste `client` si d'autres opps signées existent,
  // sinon bascule à `warm`).
  const canGoBack = !isTerminal && stageIdx > 0;
  const previousStage = canGoBack ? PIPELINE_STAGES[stageIdx - 1] : null;
  const isBusy = transitioning || refreshing;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle pb-3">
        <nav className="flex items-center gap-2 text-sm" aria-label="Chemin">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-text-muted transition-colors hover:text-accent"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Opportunités
          </button>
          <span className="text-text-muted">/</span>
          <span className="font-medium text-brand">{opp.reference}</span>
        </nav>
        <div className="flex flex-wrap items-center gap-2">
          {canGoBack && previousStage && (
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<ChevronLeft className="h-4 w-4" />}
              onClick={() => void changeStage(previousStage, 'backward')}
              disabled={isBusy}
              aria-label={`Revenir à l'étape ${STAGE_LABELS[previousStage]}`}
            >
              Revenir à « {STAGE_LABELS[previousStage]} »
            </Button>
          )}
          {!isTerminal && canAdvance && (
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<ChevronRight className="h-4 w-4" />}
              onClick={() => void changeStage(PIPELINE_STAGES[stageIdx + 1]!, 'forward')}
              disabled={isBusy}
            >
              Passer à « {STAGE_LABELS[PIPELINE_STAGES[stageIdx + 1]!]} »
            </Button>
          )}
          {/* Clôturer perdue : interdit depuis `won` (règle backend). L'utilisateur
              doit d'abord dé-signer via « Revenir à Devis émis », puis clôturer
              depuis ce stage. On masque donc le bouton sur `won` pour éviter toute
              confusion + un 422 inutile. */}
          {!isTerminal && opp.stage !== 'won' && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setCloseLostOpen(true)}
              disabled={isBusy}
            >
              Clôturer perdue
            </Button>
          )}
          {isTerminal && (
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<ChevronLeft className="h-4 w-4" />}
              onClick={handleReopen}
              disabled={isBusy}
            >
              Rouvrir
            </Button>
          )}
        </div>
      </div>

      {/* Pendant un changement d'étape ou un refresh silencieux, on remplace
          le bloc de cartes par un spinner centré. La barre d'actions et le
          breadcrumb restent stables - l'utilisateur voit que l'action est en
          cours sans que l'écran se reflow. */}
      {isBusy ? (
        <div
          className="flex flex-col items-center justify-center gap-2 py-24 text-text-muted"
          role="status"
          aria-live="polite"
          aria-label="Mise à jour de l'opportunité"
        >
          <Loader2 className="h-6 w-6 animate-spin text-accent" />
          <span className="text-xs">Mise à jour…</span>
        </div>
      ) : (
        <>
          <OpportunitySummaryCard opp={opp} onSaved={load} />
          <ChecklistSection opp={opp} checklist={checklist} onReload={load} />
          {/* Compagnies interrogées (demande client LBC 2026-05-22) — placée
              AVANT QuotesSection car le workflow naturel est : on interroge
              les compagnies puis on reçoit/uploade leurs devis. Le hook
              backend lie automatiquement upload Quote → transition inquiry
              en quote_received. reloadKey={opp.quotes.length} force un
              refetch de la liste inquiries quand un nouveau Quote est
              uploadé (le hook backend a potentiellement créé/maj une inquiry). */}
          <InquiriesSection
            opportunityId={opp.id}
            companies={companies}
            reloadKey={opp.quotes.length}
          />
          <QuotesSection opp={opp} companies={companies} onReload={load} />
        </>
      )}
      {!hideTimeline && (
        <TimelineSection prospectId={opp.prospect.id} opportunityId={opp.id} compact />
      )}

      {closeLostOpen && (
        <CloseLostModal
          onClose={() => setCloseLostOpen(false)}
          onSubmit={handleCloseLost}
          reason={closeLostReason}
          setReason={setCloseLostReason}
          comment={closeLostComment}
          setComment={setCloseLostComment}
          reattackEnabled={closeLostReattackEnabled}
          setReattackEnabled={setCloseLostReattackEnabled}
          reattackOn={closeLostReattackOn}
          setReattackOn={setCloseLostReattackOn}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Carte condensée : résumé + financier + date d'effet en un seul groupe
// ---------------------------------------------------------------------------

function OpportunitySummaryCard({
  opp,
  onSaved,
}: {
  opp: OpportunityDetail;
  onSaved: () => void | Promise<void>;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[] | null>(null);
  const [contractTypeOptions, setContractTypeOptions] = useState<ContractType[] | null>(null);
  const [apporteurOptions, setApporteurOptions] = useState<Array<{
    id: string;
    label: string;
    retrocommissionRate: number;
    retrocommissionRateFees: number;
    retrocommissionRateHonoraires: number;
  }> | null>(null);

  interface FormState {
    contractTypeId: string;
    effectiveDate: string;
    premiumHt: string;
    commissionRate: string;
    fees: string;
    honoraires: string;
    assignedUserId: string;
    apporteurId: string;
    // Refonte 2026-05-23 : `origin` déplacé sur le prospect. Plus de champ
    // ici (cf ProspectDetailPage pour modifier le canal d'acquisition).
    details: string;
  }

  // Changement de type de contrat interdit côté backend sur opp signée/clôturée
  // (cf décision produit 2026-06-01) → on masque le Select dans ces cas.
  const isTerminalOpp = opp.stage === 'won' || opp.stage === 'lost' || opp.stage === 'no_follow_up';

  function initialForm(source: OpportunityDetail): FormState {
    // Frais & honoraires : NOT NULL DEFAULT 0,00 côté DB. On affiche la
    // valeur stockée telle quelle (string Decimal). 0 → champ vide UI plus
    // léger qu'un "0,00" omniprésent.
    const feesNum = Number(source.fees);
    const honoNum = Number(source.honoraires);
    return {
      contractTypeId: source.contractType.id,
      effectiveDate: source.effectiveDate.slice(0, 10),
      premiumHt: source.premiumHt ?? '',
      commissionRate: source.commissionRate ?? '',
      fees: feesNum > 0 ? source.fees : '',
      honoraires: honoNum > 0 ? source.honoraires : '',
      assignedUserId: source.assignedUser.id,
      apporteurId: source.apporteur?.id ?? '',
      details: source.details ?? '',
    };
  }

  const [form, setForm] = useState<FormState>(() => initialForm(opp));

  // Charge la liste des collaborateurs à l'entrée en édition - évite un
  // fetch initial inutile tant que l'utilisateur ne clique pas « Modifier ».
  useEffect(() => {
    if (!editing || teamMembers !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await usersApi.teamMembers(token);
        if (!cancelled) setTeamMembers(res);
      } catch {
        if (!cancelled) setTeamMembers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, teamMembers, token]);

  // Charge les types de contrat à l'entrée en édition (sauf opp terminale,
  // où le type n'est pas modifiable). Filtre : actifs + applicables au type
  // de prospect. Le type courant est toujours inclus (même inactif) pour ne
  // pas perdre la valeur affichée dans le Select.
  useEffect(() => {
    if (!editing || isTerminalOpp || contractTypeOptions !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const all = await configApi.listContractTypes(token);
        if (cancelled) return;
        const applicable = all.filter(
          (c) =>
            c.isActive &&
            (opp.prospect.type === 'individual' ? c.applicableToIndividual : c.applicableToCompany),
        );
        // Garantit la présence du type courant (peut être inactif/non
        // applicable suite à un changement de config) pour ne pas le perdre.
        if (!applicable.some((c) => c.id === opp.contractType.id)) {
          const current = all.find((c) => c.id === opp.contractType.id);
          if (current) applicable.unshift(current);
        }
        setContractTypeOptions(applicable);
      } catch {
        if (!cancelled) setContractTypeOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, isTerminalOpp, contractTypeOptions, token, opp.prospect.type, opp.contractType.id]);

  // Charge la liste des apporteurs actifs à l'entrée en édition. Si l'opp a
  // déjà un apporteur désactivé attribué historiquement, on l'inclut quand
  // même dans les options pour préserver la valeur affichée.
  useEffect(() => {
    if (!editing || apporteurOptions !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const list = await apporteursApi.list(token, { is_active: true });
        if (cancelled) return;
        const options = list.map((a) => {
          const base = apporteurDisplayName(a);
          const label = a.type === 'person' && a.company ? `${base} (${a.company})` : base;
          return {
            id: a.id,
            label,
            retrocommissionRate: Number(a.retrocommissionRate),
            retrocommissionRateFees: Number(a.retrocommissionRateFees),
            retrocommissionRateHonoraires: Number(a.retrocommissionRateHonoraires),
          };
        });
        // Si l'opp a un apporteur désactivé, on l'ajoute en début de liste
        // avec un label « (désactivé) » pour ne pas perdre la valeur.
        if (opp.apporteur && !opp.apporteur.isActive) {
          const apBase = apporteurDisplayName(opp.apporteur);
          const apSuffix =
            opp.apporteur.type === 'person' && opp.apporteur.company
              ? ` (${opp.apporteur.company})`
              : '';
          options.unshift({
            id: opp.apporteur.id,
            label: `${apBase}${apSuffix} — désactivé`,
            retrocommissionRate: Number(opp.apporteur.retrocommissionRate),
            retrocommissionRateFees: Number(opp.apporteur.retrocommissionRateFees),
            retrocommissionRateHonoraires: Number(opp.apporteur.retrocommissionRateHonoraires),
          });
        }
        setApporteurOptions(options);
      } catch {
        if (!cancelled) setApporteurOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, apporteurOptions, token, opp.apporteur]);

  function startEdit() {
    setForm(initialForm(opp));
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setForm(initialForm(opp));
  }

  async function handleSave() {
    setSaving(true);
    try {
      // Payload partiel : seuls les champs modifiés sont envoyés. Les valeurs
      // vides ("") pour les primes/taux deviennent `null` via un update qui
      // supprime le champ - non supporté par le schéma actuel (number ou
      // absent). On omet donc les champs vides : l'opération = « ne pas
      // toucher à la valeur actuelle ». Pour effacer une valeur, il faudrait
      // étendre le schéma (hors scope V1).
      // `premiumTtc` retiré de l'UI 2026-04-27. Le champ DB est préservé
      // (premium_ttc nullable) pour réactivation future.
      const body: Record<string, unknown> = {
        effectiveDate: form.effectiveDate,
        assignedUserId: form.assignedUserId,
      };
      // Type de contrat : envoyé seulement s'il a changé (le backend valide
      // existence / actif / applicabilité et détache les liens checklist de
      // l'ancien type ; interdit sur opp signée/clôturée).
      if (form.contractTypeId && form.contractTypeId !== opp.contractType.id) {
        body.contractTypeId = form.contractTypeId;
      }
      if (form.premiumHt !== '') body.premiumHt = Number(form.premiumHt);
      if (form.commissionRate !== '') body.commissionRate = Number(form.commissionRate);
      // Frais & honoraires : champ vide = pas de mutation (reste à la valeur
      // précédente, généralement 0). Sinon valeur saisie (acceptée [0..]).
      if (form.fees !== '') body.fees = Number(form.fees);
      if (form.honoraires !== '') body.honoraires = Number(form.honoraires);
      // Apporteur : `null` pour retirer (schema Zod accepte nullable),
      // ou un UUID pour attribuer. Backend re-vérifie qu'il existe et
      // est actif (sinon 422). Le montant de rétrocommission est
      // calculé à la volée côté backend (taux apporteur × commission)
      // depuis 2026-04-28 - plus de saisie manuelle.
      body.apporteurId = form.apporteurId || null;
      // Refonte 2026-05-23 : `origin` déplacé sur le prospect — plus envoyé ici.
      // Détails : trim + envoi null pour effacement volontaire (schema Zod
      // update accepte `null` pour ce champ, contrairement aux montants).
      const trimmedDetails = form.details.trim();
      body.details = trimmedDetails === '' ? null : trimmedDetails;

      await opportunitiesApi.update(token, opp.id, body);
      toast({ tone: 'success', message: 'Opportunité mise à jour.' });
      setEditing(false);
      await onSaved();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Mise à jour impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    } finally {
      setSaving(false);
    }
  }

  const formattedDate = new Date(opp.effectiveDate).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {/* Badge placé à droite du titre, légèrement remonté par rapport
              à la baseline pour un rendu « inset » à la Stripe. */}
          <div className="flex items-center gap-2">
            <CardTitle className="!mb-0">{opp.contractType.label}</CardTitle>
            <PipelineStageBadge stage={opp.stage} size="lg" className="-mt-0.5" />
          </div>
          <CardDescription>
            Créée le {new Date(opp.createdAt).toLocaleDateString('fr-FR')}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {!editing && (
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Pencil className="h-3.5 w-3.5" />}
              onClick={startEdit}
            >
              Modifier
            </Button>
          )}
        </div>
      </div>

      {opp.lossReason && (
        <div className="mt-3 rounded-md border border-danger-100 bg-danger-50 px-3 py-2 text-xs text-danger-600">
          <span className="font-semibold">Motif de perte :</span>{' '}
          {LOSS_REASON_LABELS[opp.lossReason]}
          {opp.lossComment && <span className="text-text-muted"> - {opp.lossComment}</span>}
        </div>
      )}

      {/* Mode lecture - grille « Stripe-like » : 3 colonnes denses, labels en
          uppercase micro, valeurs en brand. Dividers légers entre sections. */}
      {!editing && (
        <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-border-subtle pt-5 text-sm sm:grid-cols-3">
          <Field label="Commercial">
            {opp.assignedUser.firstName} {opp.assignedUser.lastName}
          </Field>
          <Field label="Date d'effet">{formattedDate}</Field>
          {/* Date de signature estimée — visible uniquement si saisie via
              la modale au 1er « Envoyé » (cf refonte 2026-05-22). Reset à
              null quand l'opp quitte quote_sent → on n'affiche rien. */}
          {opp.estimatedSigningDate && (
            <Field label="Signature estimée" hint="saisie au 1er envoi de devis">
              {new Date(opp.estimatedSigningDate).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </Field>
          )}
          {/* Refonte 2026-05-23 : Field « Origine » retiré. Le canal
              d'acquisition est désormais visible sur la fiche prospect. */}
          <Field label="Taux commission">
            {opp.commissionRate !== null ? `${formatNum(opp.commissionRate)} %` : '-'}
          </Field>
          {/* Layout 2x2 financier (décision 2026-04-30 Q12) :
                Ligne 1 : Prime HT  · Commission (calculée)
                Ligne 2 : Frais     · Honoraires
              + ligne récap "Total CA = commission + frais + honoraires" */}
          <Field label="Prime HT">
            {opp.premiumHt !== null ? `${formatNum(opp.premiumHt)} €` : '-'}
          </Field>
          <Field label="Commission" hint="calculée">
            {opp.commissionAmount !== null ? `${formatNum(opp.commissionAmount)} €` : '-'}
          </Field>
          <Field label="Frais">{Number(opp.fees) > 0 ? `${formatNum(opp.fees)} €` : '-'}</Field>
          <Field label="Honoraires">
            {Number(opp.honoraires) > 0 ? `${formatNum(opp.honoraires)} €` : '-'}
          </Field>
          {/* Total CA — somme com + frais + honoraires (colonne GENERATED
              total_revenue côté DB). Affiché span=3 sous les 4 champs financiers
              pour une mise en avant Stripe-like. */}
          {opp.totalRevenue !== null && Number(opp.totalRevenue) > 0 && (
            <Field label="Total CA" hint="com + frais + honoraires" span={2}>
              <span className="text-base font-semibold text-brand">
                {formatNum(opp.totalRevenue)} €
              </span>
            </Field>
          )}
          {opp.apporteur &&
            (() => {
              // Rétrocoms calculées à la volée — 3 composantes (décision
              // 2026-04-30 Q B) : un montant par taux apporteur, total
              // affiché en gras et breakdown sub-info en 3 lignes.
              const rateCom = Number(opp.apporteur.retrocommissionRate);
              const rateFees = Number(opp.apporteur.retrocommissionRateFees);
              const rateHono = Number(opp.apporteur.retrocommissionRateHonoraires);
              const commission = opp.commissionAmount !== null ? Number(opp.commissionAmount) : 0;
              const fees = Number(opp.fees);
              const hono = Number(opp.honoraires);
              const retroCom = (commission * rateCom) / 100;
              const retroFees = (fees * rateFees) / 100;
              const retroHono = (hono * rateHono) / 100;
              const retroTotal = retroCom + retroFees + retroHono;
              return (
                <>
                  <Field label="Apporteur" span={2}>
                    {apporteurDisplayName(opp.apporteur)}
                    {opp.apporteur.type === 'person' && opp.apporteur.company && (
                      <span className="text-text-muted"> · {opp.apporteur.company}</span>
                    )}
                    {!opp.apporteur.isActive && (
                      <span className="ml-2 text-xs text-text-muted">(désactivé)</span>
                    )}
                  </Field>
                  <Field label="Rétrocommission totale" span={3}>
                    <div className="space-y-1">
                      <div className="text-base font-semibold text-brand">
                        {formatNum(retroTotal.toFixed(2))} €
                      </div>
                      <div className="text-[11px] leading-relaxed text-text-muted">
                        <div>
                          Sur commission : {formatNum(retroCom.toFixed(2))} €
                          <span className="ml-1">({rateCom.toString().replace('.', ',')} %)</span>
                        </div>
                        <div>
                          Sur frais : {formatNum(retroFees.toFixed(2))} €
                          <span className="ml-1">({rateFees.toString().replace('.', ',')} %)</span>
                        </div>
                        <div>
                          Sur honoraires : {formatNum(retroHono.toFixed(2))} €
                          <span className="ml-1">({rateHono.toString().replace('.', ',')} %)</span>
                        </div>
                      </div>
                    </div>
                  </Field>
                </>
              );
            })()}
        </dl>
      )}

      {/* Bloc Détails en lecture - plain text avec conservation des sauts
          de ligne. Divider léger pour ancrer la section. Masqué si vide
          pour ne pas alourdir la fiche. */}
      {!editing && opp.details && (
        <div className="mt-5 border-t border-border-subtle pt-5">
          <dt className="text-[11px] font-medium uppercase tracking-wider text-text-muted">
            Détails
          </dt>
          <dd className="mt-1.5 whitespace-pre-wrap text-sm text-text-primary">{opp.details}</dd>
        </div>
      )}

      {/* Mode édition : mêmes cases, mais inputs. Champs non éditables
          (type de contrat, montants calculés, stage) ne sont pas ré-affichés
          pour garder la forme compacte. */}
      {editing && (
        <div className="mt-5 space-y-4 border-t border-border-subtle pt-5">
          {/* Type de contrat : éditable uniquement hors opp signée/clôturée
              (le backend renvoie 422 sinon). Filtré par type de prospect. */}
          {!isTerminalOpp && (
            <div>
              <Label htmlFor="edit-contract-type">Type de contrat</Label>
              <Select
                id="edit-contract-type"
                value={form.contractTypeId}
                onChange={(e) => setForm({ ...form, contractTypeId: e.target.value })}
                disabled={contractTypeOptions === null}
              >
                {contractTypeOptions === null ? (
                  <option value={form.contractTypeId}>Chargement…</option>
                ) : (
                  contractTypeOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} - {c.label}
                    </option>
                  ))
                )}
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="edit-assigned">Commercial</Label>
              <Select
                id="edit-assigned"
                value={form.assignedUserId}
                onChange={(e) => setForm({ ...form, assignedUserId: e.target.value })}
                disabled={teamMembers === null}
              >
                {teamMembers === null ? (
                  <option value={form.assignedUserId}>Chargement…</option>
                ) : (
                  teamMembers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.firstName} {m.lastName}
                    </option>
                  ))
                )}
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-date">Date d&apos;effet</Label>
              <Input
                id="edit-date"
                type="date"
                value={form.effectiveDate}
                onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
              />
            </div>
            {/* Refonte 2026-05-23 : édition « Origine » retirée. Modifiable
                désormais sur la fiche prospect (canal d'acquisition stable
                par client, plus par opportunité). */}
          </div>
          {/* Bloc financier 2x2 — décision 2026-04-30 Q12 :
                Ligne 1 : Prime HT  · Taux commission
                Ligne 2 : Frais     · Honoraires
              Prime TTC retirée 2026-04-27, champ DB préservé. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="edit-ht">Prime HT (€)</Label>
              <Input
                id="edit-ht"
                type="number"
                step="0.01"
                min="0"
                value={form.premiumHt}
                onChange={(e) => setForm({ ...form, premiumHt: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-rate">Taux commission (%)</Label>
              <Input
                id="edit-rate"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={form.commissionRate}
                onChange={(e) => setForm({ ...form, commissionRate: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-fees">Frais (€)</Label>
              <Input
                id="edit-fees"
                type="number"
                step="0.01"
                min="0"
                value={form.fees}
                onChange={(e) => setForm({ ...form, fees: e.target.value })}
                placeholder="0,00"
              />
            </div>
            <div>
              <Label htmlFor="edit-honoraires">Honoraires (€)</Label>
              <Input
                id="edit-honoraires"
                type="number"
                step="0.01"
                min="0"
                value={form.honoraires}
                onChange={(e) => setForm({ ...form, honoraires: e.target.value })}
                placeholder="0,00"
              />
            </div>
          </div>
          {/* Total CA récap (live) — somme commission + frais + honoraires.
              Décision 2026-04-30 (Q C) : 2 décimales, format français. */}
          {(() => {
            const ht = Number(form.premiumHt);
            const rate = Number(form.commissionRate);
            const commission = Number.isFinite(ht) && Number.isFinite(rate) ? (ht * rate) / 100 : 0;
            const fees = Number(form.fees) || 0;
            const hono = Number(form.honoraires) || 0;
            const total = commission + fees + hono;
            if (total <= 0) return null;
            return (
              <div className="rounded-md border border-border-subtle bg-gray-50 px-3 py-2 text-sm">
                <span className="text-text-muted">Total CA estimé : </span>
                <span className="font-semibold text-brand">
                  {total.toLocaleString('fr-FR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{' '}
                  €
                </span>
              </div>
            );
          })()}
          {/* Apporteur d'affaires (optionnel). Si sélectionné, on affiche
              en lecture seule la rétrocommission calculée à la volée
              (= commission × retrocommissionRate / 100) - décision produit
              2026-04-28. Plus de saisie manuelle, le taux vient de la
              fiche apporteur. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="edit-apporteur">Apporteur</Label>
              <Select
                id="edit-apporteur"
                value={form.apporteurId}
                onChange={(e) => {
                  setForm({ ...form, apporteurId: e.target.value });
                }}
                disabled={apporteurOptions === null}
              >
                <option value="">— Aucun</option>
                {(apporteurOptions ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </Select>
            </div>
            {form.apporteurId &&
              (() => {
                // Breakdown rétrocom 3 composantes (décision 2026-04-30 Q B).
                // Calcul live à partir des taux apporteur + montants saisis.
                const a = (apporteurOptions ?? []).find((x) => x.id === form.apporteurId);
                if (!a) return null;
                const ht = Number(form.premiumHt);
                const rate = Number(form.commissionRate);
                const commission =
                  Number.isFinite(ht) && Number.isFinite(rate) ? (ht * rate) / 100 : 0;
                const fees = Number(form.fees) || 0;
                const hono = Number(form.honoraires) || 0;
                const retroCom = (commission * a.retrocommissionRate) / 100;
                const retroFees = (fees * a.retrocommissionRateFees) / 100;
                const retroHono = (hono * a.retrocommissionRateHonoraires) / 100;
                const retroTotal = retroCom + retroFees + retroHono;
                const fmt = (v: number) =>
                  v.toLocaleString('fr-FR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                return (
                  <div>
                    <Label>Rétrocommission totale</Label>
                    <div className="rounded-md border border-border-subtle bg-gray-50 px-3 py-2 text-sm">
                      <div className="font-semibold text-brand">{fmt(retroTotal)} €</div>
                      <div className="mt-1 space-y-0.5 text-[11px] text-text-muted">
                        <div>
                          Sur com : {fmt(retroCom)} € (
                          {a.retrocommissionRate.toString().replace('.', ',')} %)
                        </div>
                        <div>
                          Sur frais : {fmt(retroFees)} € (
                          {a.retrocommissionRateFees.toString().replace('.', ',')} %)
                        </div>
                        <div>
                          Sur hono : {fmt(retroHono)} € (
                          {a.retrocommissionRateHonoraires.toString().replace('.', ',')} %)
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
          </div>
          <div>
            <Label htmlFor="edit-details">Détails</Label>
            <Textarea
              id="edit-details"
              rows={5}
              maxLength={5000}
              value={form.details}
              onChange={(e) => setForm({ ...form, details: e.target.value })}
              placeholder="Contexte client, points d'attention, historique des échanges…"
            />
            <p className="mt-1 text-[11px] text-text-muted">
              {form.details.length}/5000 caractères
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" size="sm" onClick={cancelEdit} disabled={saving}>
              Annuler
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>
              Enregistrer
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Field({
  label,
  hint,
  children,
  span,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  span?: 1 | 2 | 3;
}) {
  const spanClass = span === 3 ? 'sm:col-span-3' : span === 2 ? 'sm:col-span-2' : '';
  return (
    <div className={spanClass}>
      <dt className="text-[11px] uppercase tracking-wider text-text-muted">
        {label}
        {hint && <span className="ml-1 text-[9px] text-text-muted">({hint})</span>}
      </dt>
      <dd className="mt-1 font-medium text-brand">{children}</dd>
    </div>
  );
}

function formatNum(v: string | number): string {
  return Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

// ---------------------------------------------------------------------------
// Score - composant autonome pour la sidebar (au-dessus de la timeline)
// ---------------------------------------------------------------------------

/**
 * Palette sémantique du score d'opportunité (codes Stripe-inspired) :
 *  - 0-29 : rouge « danger » - opportunité en difficulté
 *  - 30-59 : orange « warning » - opportunité tiède
 *  - 60-79 : violet accent Stripe - progression correcte
 *  - 80-100 : vert « success » - proche de la signature
 *
 * Seul le nombre est coloré (demande explicite) ; le reste de la carte reste
 * dans la palette neutre. Les classes Tailwind utilisées sont celles du thème
 * projet (`text-danger`, `text-warning-600`, `text-accent`, `text-success-600`).
 */
function scoreTone(score: number): string {
  if (score < 30) return 'text-danger';
  if (score < 60) return 'text-warning-600';
  if (score < 80) return 'text-accent';
  return 'text-success-600';
}

export function OpportunityScoreCard({ opportunityId }: { opportunityId: string }) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [score, setScore] = useState<ScoreBreakdown | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadScore = useCallback(async () => {
    try {
      const res = await opportunitiesApi.scoreDetails(token, opportunityId);
      setScore(res);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }, [token, opportunityId]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setScore(null);
    void (async () => {
      try {
        const res = await opportunitiesApi.scoreDetails(token, opportunityId);
        if (!cancelled) setScore(res);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erreur');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opportunityId, token]);

  // Realtime : recalcul du score à chaque mutation de l'opp (changeStage,
  // addEvent, uploadDoc, quote-select). Payload minimal `{ id, actorId }` -
  // on refetch via API authentifiée, RBAC rejoué côté serveur. Filtre
  // strict par opportunityId pour ne pas spammer de fetch si d'autres opps
  // sont mutées en parallèle.
  useRealtimeEvent<OpportunityUpdatedEvent>(
    REALTIME_EVENTS.OPPORTUNITY_UPDATED,
    useCallback(
      (ev) => {
        if (ev.id === opportunityId) void loadScore();
      },
      [opportunityId, loadScore],
    ),
  );

  if (error) {
    return (
      <Card>
        <Banner tone="danger">{error}</Banner>
      </Card>
    );
  }
  if (!score) {
    return (
      <Card>
        <Skeleton className="h-32 w-full" />
      </Card>
    );
  }

  const ctx = score.context;

  // Explication humaine de chaque sous-score. Affichée au hover de la ligne -
  // la valeur reste en clair, le contexte est là pour ceux qui veulent
  // comprendre le chiffre sans aller lire les specs.
  const rows: Array<{
    label: string;
    value: number;
    weight: string;
    weightPct: number;
    tooltip: React.ReactNode;
  }> = [
    {
      label: 'Étape',
      value: score.stage,
      weight: '40 %',
      weightPct: 40,
      tooltip: (
        <div className="space-y-1.5">
          <p className="font-semibold">Étape du pipeline</p>
          <p className="text-white/80">
            Chaque étape a un score prédéfini. Passer à l&apos;étape suivante augmente
            automatiquement cette composante.
          </p>
          <ul className="text-white/90">
            <li>Prospect identifié : 5 · Prise de contact : 15</li>
            <li>Qualification : 30 · RDV effectué : 45</li>
            <li>Devis émis : 60 · Signé : 100</li>
          </ul>
        </div>
      ),
    },
    {
      label: 'Ancienneté',
      value: score.age,
      weight: '20 %',
      weightPct: 20,
      tooltip: (
        <div className="space-y-1.5">
          <p className="font-semibold">Ancienneté</p>
          <p className="text-white/80">
            100 % si activité dans les 30 derniers jours. Au-delà, décote de 10 points par tranche
            de 15 jours d&apos;inactivité.
          </p>
          <p className="text-white/90">Dernière activité : il y a {ctx.daysSinceActivity} j</p>
        </div>
      ),
    },
    {
      label: 'Activité',
      value: score.activity,
      weight: '20 %',
      weightPct: 20,
      tooltip: (
        <div className="space-y-1.5">
          <p className="font-semibold">Activité récente</p>
          <p className="text-white/80">
            Ratio nombre d&apos;événements sur les {ctx.activityWindowDays} derniers jours / moyenne
            de référence ({ctx.activityReferenceMean}), cappé à 100 %.
          </p>
          <p className="text-white/90">
            {ctx.eventsInWindow} événement{ctx.eventsInWindow > 1 ? 's' : ''} sur la période
          </p>
        </div>
      ),
    },
    {
      label: 'Complétude',
      value: score.completion,
      weight: '20 %',
      weightPct: 20,
      tooltip: (
        <div className="space-y-1.5">
          <p className="font-semibold">Complétude documentaire</p>
          <p className="text-white/80">
            Ratio de documents requis déposés sur la checklist du type de contrat. 100 % si aucun
            document requis.
          </p>
          <p className="text-white/90">
            {ctx.providedDocs} document{ctx.providedDocs > 1 ? 's' : ''} déposés /{' '}
            {ctx.requiredDocs} requis
          </p>
        </div>
      ),
    },
  ];

  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <div>
          <CardTitle>Scoring</CardTitle>
          <CardDescription>Calcul automatique - hover pour la règle.</CardDescription>
        </div>
        <Tooltip
          side="bottom"
          align="end"
          content={
            <div className="space-y-1.5">
              <p className="font-semibold">Comment est calculé le score ?</p>
              <p className="text-white/80">
                Pondération automatique de 4 facteurs, reflétant l&apos;avancée réelle de
                l&apos;opportunité :
              </p>
              <ul className="text-white/90">
                <li>Étape pipeline (40 %)</li>
                <li>Ancienneté (20 %)</li>
                <li>Activité récente (20 %)</li>
                <li>Complétude documentaire (20 %)</li>
              </ul>
            </div>
          }
        >
          <Badge tone="neutral" className="cursor-help">
            Auto
          </Badge>
        </Tooltip>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className={cn('text-[40px] font-semibold leading-none', scoreTone(score.score))}>
          {score.score}
        </span>
        <span className="text-sm text-text-muted">/ 100</span>
      </div>
      <dl className="mt-4 space-y-2">
        {rows.map((r) => (
          <Tooltip
            key={r.label}
            side="top"
            align="start"
            className="block w-full"
            content={r.tooltip}
          >
            <div className="flex w-full cursor-help items-center justify-between gap-2 text-xs">
              <span className="text-text-muted">
                {r.label} <span className="text-gray-400">{r.weight}</span>
              </span>
              <span className="font-medium text-brand">{r.value}</span>
            </div>
          </Tooltip>
        ))}
      </dl>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Quotes
// ---------------------------------------------------------------------------

function QuotesSection({
  opp,
  companies,
  onReload,
}: {
  opp: OpportunityDetail;
  companies: Company[];
  onReload: () => void | Promise<void>;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const confirm = useConfirm();
  const [uploading, setUploading] = useState(false);
  const [companyId, setCompanyId] = useState('');
  // Devis en attente de confirmation « Signer » (ex « Retenir ») via modale
  // — décision produit 2026-04-27 : permettre l'ajustement date d'effet /
  // prime HT / commission % avant le passage à `won` automatique.
  const [selectQuoteId, setSelectQuoteId] = useState<string | null>(null);
  // Refonte 2026-05-22 : devis en attente de confirmation « Envoyé » via
  // modale (saisie date de signature estimée). Ouverte UNIQUEMENT au 1er
  // Envoyé sur l'opp (i.e. opp.estimatedSigningDate IS NULL). Les Envoyés
  // suivants posent juste sentAt sans modale.
  const [markSentQuote, setMarkSentQuote] = useState<Quote | null>(null);

  async function handleQuoteFiles(files: File[]) {
    if (!companyId) {
      toast({
        tone: 'danger',
        title: 'Compagnie requise',
        message: 'Sélectionnez une compagnie avant de déposer un devis.',
      });
      return;
    }
    setUploading(true);
    const failures: string[] = [];
    let ok = 0;
    // Upload séquentiel : 1 devis par fichier, rattaché à la compagnie choisie.
    for (const f of files) {
      try {
        await opportunitiesApi.quotes.upload(token, opp.id, f, companyId);
        ok++;
      } catch (err) {
        failures.push(`${f.name} : ${err instanceof Error ? err.message : 'erreur'}`);
      }
    }
    setUploading(false);
    await onReload();
    if (failures.length === 0) {
      toast({ tone: 'success', title: 'Upload terminé', message: `${ok} devis uploadé(s)` });
    } else {
      toast({
        tone: 'danger',
        title: `${failures.length} échec(s)${ok ? ` · ${ok} OK` : ''}`,
        message: failures.join(' · '),
      });
    }
  }

  async function act(id: string, action: 'mark-sent' | 'select' | 'reject' | 'delete') {
    try {
      if (action === 'mark-sent') {
        // Refonte 2026-06-18 : la modale s'ouvre à CHAQUE envoi pour saisir la
        // date d'envoi réelle du devis (chaque devis peut être envoyé un jour
        // différent). La date de signature estimée n'est demandée qu'au 1er
        // envoi de l'opp (cf prop askSigningDate de la modale).
        const quote = opp.quotes.find((q) => q.id === id) ?? null;
        if (!quote) return;
        setMarkSentQuote(quote);
        return;
      } else if (action === 'select') {
        // Pas d'appel direct - on ouvre la modale qui demande confirmation
        // (et permet d'ajuster effective_date / premium_ht / commission_rate
        // avant la transition automatique vers `won`).
        setSelectQuoteId(id);
        return;
      } else if (action === 'reject') {
        await opportunitiesApi.quotes.reject(token, id);
      } else if (action === 'delete') {
        const ok = await confirm({
          title: 'Supprimer ce devis ?',
          variant: 'danger',
          confirmLabel: 'Supprimer',
        });
        if (!ok) return;
        await opportunitiesApi.quotes.delete(token, id);
      }
      await onReload();
    } catch (err) {
      toast({
        tone: 'danger',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  /** Handler du submit de la modale EstimatedSigningDateModal (à chaque envoi). */
  async function handleMarkSentConfirm(data: {
    quoteSentDate: string;
    estimatedSigningDate?: string;
  }) {
    if (!markSentQuote) return;
    try {
      await opportunitiesApi.quotes.markSent(token, markSentQuote.id, data);
      toast({
        tone: 'success',
        title: 'Devis envoyé',
        message: data.estimatedSigningDate
          ? `${markSentQuote.fileName} marqué envoyé. Tâche de relance créée pour le ${new Date(`${data.estimatedSigningDate}T00:00:00Z`).toLocaleDateString('fr-FR')}.`
          : `${markSentQuote.fileName} marqué comme envoyé le ${new Date(`${data.quoteSentDate}T00:00:00Z`).toLocaleDateString('fr-FR')}.`,
      });
      setMarkSentQuote(null);
      await onReload();
    } catch (err) {
      // Toast affiché par la modale elle-même via setError (rejet promise) +
      // un toast global ici en filet de sécurité.
      toast({
        tone: 'danger',
        title: 'Envoi refusé',
        message: err instanceof Error ? err.message : 'Erreur',
      });
      // Re-throw pour que la modale affiche le Banner d'erreur interne.
      throw err;
    }
  }

  /**
   * Confirmation de la sélection devis depuis la modale. Si l'utilisateur a
   * modifié au moins une des 3 valeurs (date d'effet, prime HT, commission %),
   * on PATCH l'opportunité d'abord, puis on `select` le devis (ce qui
   * déclenche l'Automation #9 côté backend : passage à `won` + recompute du
   * score et du potentiel sur les NOUVELLES valeurs).
   */
  async function handleSelectQuoteConfirm(values: {
    effectiveDate: string;
    premiumHt: number;
    commissionRate: number;
  }) {
    if (!selectQuoteId) return;
    const id = selectQuoteId;
    try {
      const currentEffectiveDate = opp.effectiveDate.slice(0, 10);
      const currentPremiumHt = Number(opp.premiumHt ?? 0);
      const currentCommissionRate = Number(opp.commissionRate ?? 0);
      const changed =
        values.effectiveDate !== currentEffectiveDate ||
        values.premiumHt !== currentPremiumHt ||
        values.commissionRate !== currentCommissionRate;
      if (changed) {
        await opportunitiesApi.update(token, opp.id, {
          effectiveDate: values.effectiveDate,
          premiumHt: values.premiumHt,
          commissionRate: values.commissionRate,
        });
      }
      await opportunitiesApi.quotes.select(token, id);
      const willAutoSign =
        opp.stage !== 'won' && opp.stage !== 'lost' && opp.stage !== 'no_follow_up';
      toast({
        tone: 'success',
        title: willAutoSign ? 'Opportunité passée à « Signé »' : 'Devis signé',
        message: changed
          ? "Valeurs de l'opportunité mises à jour, devis signé."
          : willAutoSign
            ? 'Le devis signé a déclenché la signature automatique.'
            : 'Le devis est marqué comme signé.',
      });
      setSelectQuoteId(null);
      await onReload();
    } catch (err) {
      toast({
        tone: 'danger',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  async function download(id: string) {
    try {
      const { url } = await opportunitiesApi.quotes.downloadUrl(token, id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast({
        tone: 'danger',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  return (
    <Card>
      <div className="mb-3 flex items-start justify-between">
        <div>
          <CardTitle>Devis</CardTitle>
          <CardDescription>Upload PDF - 10 Mo max, validation magic bytes.</CardDescription>
        </div>
      </div>

      <QuoteList quotes={opp.quotes} onAct={act} onDownload={download} />

      <div className="mt-4 space-y-3 rounded-md border border-dashed border-border-subtle bg-gray-50 p-4">
        <div>
          <Label htmlFor="company">Compagnie</Label>
          <Select
            id="company"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            disabled={companies.length === 0}
          >
            <option value="">-</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          {companies.length === 0 && (
            <p className="mt-1 text-xs text-text-muted">
              Aucune compagnie active - ajoutez-en dans la Configuration.
            </p>
          )}
        </div>
        <Dropzone
          onFiles={handleQuoteFiles}
          disabled={uploading || !companyId || companies.length === 0}
          hint="Devis PDF, scan (PNG/JPG) ou mail Outlook (.msg/.eml) · max 25 Mo"
        />
        {!companyId && companies.length > 0 && (
          <p className="text-xs text-text-muted">
            Sélectionnez une compagnie pour activer le dépôt de devis.
          </p>
        )}
      </div>
      <SelectQuoteConfirmModal
        open={selectQuoteId !== null}
        opp={opp}
        onClose={() => setSelectQuoteId(null)}
        onConfirm={handleSelectQuoteConfirm}
      />
      {markSentQuote && (
        <EstimatedSigningDateModal
          quoteFileName={markSentQuote.fileName}
          askSigningDate={!opp.estimatedSigningDate}
          onClose={() => setMarkSentQuote(null)}
          onSubmit={handleMarkSentConfirm}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Modale de confirmation « Retenir devis » (décision produit 2026-04-27)
// ---------------------------------------------------------------------------
//
// Affichée juste avant le `selectQuote` qui déclenche l'Automation #9
// (passage `won` + signature). Permet à l'utilisateur de figer ou ajuster
// 3 valeurs critiques de l'opp avant la signature : date d'effet, prime HT,
// pourcentage de commission. Si modifiées, l'opp est PATCHée puis le devis
// est sélectionné dans la foulée (commission_amount et weighted_revenue
// se recalculent automatiquement sur les nouvelles valeurs - colonnes
// générées Postgres).

function SelectQuoteConfirmModal({
  open,
  opp,
  onClose,
  onConfirm,
}: {
  open: boolean;
  opp: OpportunityDetail;
  onClose: () => void;
  onConfirm: (values: {
    effectiveDate: string;
    premiumHt: number;
    commissionRate: number;
  }) => Promise<void>;
}) {
  const [effectiveDate, setEffectiveDate] = useState(opp.effectiveDate.slice(0, 10));
  const [premiumHt, setPremiumHt] = useState(opp.premiumHt ?? '');
  const [commissionRate, setCommissionRate] = useState(opp.commissionRate ?? '');
  const [submitting, setSubmitting] = useState(false);

  // Re-pré-remplir à chaque ouverture (peut différer si l'opp a été modifiée
  // entre 2 ouvertures de la même modale).
  useEffect(() => {
    if (open) {
      setEffectiveDate(opp.effectiveDate.slice(0, 10));
      setPremiumHt(opp.premiumHt ?? '');
      setCommissionRate(opp.commissionRate ?? '');
    }
  }, [open, opp]);

  // Escape key fermeture.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const premiumHtNum = Number(premiumHt);
  const commissionRateNum = Number(commissionRate);
  const isValid =
    /^\d{4}-\d{2}-\d{2}$/.test(effectiveDate) &&
    Number.isFinite(premiumHtNum) &&
    premiumHtNum > 0 &&
    Number.isFinite(commissionRateNum) &&
    commissionRateNum >= 0 &&
    commissionRateNum <= 100;

  const computedCommission = isValid ? (premiumHtNum * commissionRateNum) / 100 : null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm({
        effectiveDate,
        premiumHt: premiumHtNum,
        commissionRate: commissionRateNum,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-modal"
      >
        <h2 className="text-lg font-semibold text-brand">Retenir ce devis</h2>
        <p className="mt-1 text-sm text-text-muted">
          Confirmez ou ajustez les valeurs de l&apos;opportunité avant signature. Si vous modifiez,
          l&apos;opp est mise à jour avant que le devis soit marqué comme retenu.
        </p>
        <div className="mt-5 space-y-4">
          <div>
            <Label htmlFor="confirm-effective-date">Date d&apos;effet</Label>
            <Input
              id="confirm-effective-date"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="confirm-premium-ht">Prime HT (€)</Label>
            <Input
              id="confirm-premium-ht"
              type="number"
              min="0"
              step="0.01"
              value={premiumHt}
              onChange={(e) => setPremiumHt(e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="confirm-commission-rate">% commission</Label>
            <Input
              id="confirm-commission-rate"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              required
            />
            {computedCommission !== null && (
              <p className="mt-1 text-xs text-text-muted">
                Commission annuelle calculée :{' '}
                <span className="font-semibold text-brand">
                  {computedCommission.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €
                </span>
              </p>
            )}
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" disabled={!isValid} loading={submitting}>
            Confirmer et retenir
          </Button>
        </div>
      </form>
    </div>
  );
}

function QuoteList({
  quotes,
  onAct,
  onDownload,
}: {
  quotes: Quote[];
  onAct: (id: string, action: 'mark-sent' | 'select' | 'reject' | 'delete') => void;
  onDownload: (id: string) => void;
}) {
  if (quotes.length === 0) {
    return (
      <EmptyState
        variant="list"
        title="Aucun devis"
        description="Uploadez le premier PDF de devis."
      />
    );
  }
  // Refonte 2026-05-22 : sémantique du statut + sentAt.
  //   - sentAt=null + status=pending : brouillon interne (préparation)
  //   - sentAt set + status=pending  : envoyé au client, en attente retour
  //   - status=selected              : signé (renommage « Retenu » → « Signé »)
  //   - status=rejected              : rejeté
  // « Signer » n'apparaît que si sentAt set. « Envoyer » n'apparaît que si sentAt null.
  return (
    <ul className="divide-y divide-gray-100">
      {quotes.map((q) => {
        const isSent = q.sentAt !== null;
        return (
          <li key={q.id} className="flex items-center justify-between gap-3 py-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-brand">{q.fileName}</span>
                {q.status === 'selected' && <Badge tone="success">Signé</Badge>}
                {q.status === 'rejected' && <Badge tone="neutral">Rejeté</Badge>}
                {q.status === 'pending' && isSent && <Badge tone="accent">Envoyé</Badge>}
                {q.status === 'pending' && !isSent && <Badge tone="neutral">Brouillon</Badge>}
              </div>
              <div className="mt-0.5 text-xs text-text-muted">
                {q.insuranceCompany.name} · {new Date(q.createdAt).toLocaleDateString('fr-FR')} ·{' '}
                {(q.fileSize / 1024).toFixed(0)} Ko
                {isSent && q.sentAt && (
                  <>
                    {' · '}
                    <span className="text-accent-600">
                      Envoyé le {new Date(q.sentAt).toLocaleDateString('fr-FR')}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex shrink-0 gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDownload(q.id)}
                leadingIcon={<Download className="h-3.5 w-3.5" />}
              >
                Télécharger
              </Button>
              {/* Bouton « Envoyer » : visible uniquement tant que le devis
                  n'a pas été envoyé (action à faire, pas état passé). Ouvre la
                  modale de saisie de la date d'envoi (+ signature estimée au
                  1er envoi de l'opp). */}
              {!isSent && q.status !== 'rejected' && (
                <Button
                  variant="ghost"
                  size="sm"
                  leadingIcon={<Send className="h-3.5 w-3.5" />}
                  onClick={() => onAct(q.id, 'mark-sent')}
                >
                  Envoyer
                </Button>
              )}
              {/* Bouton « Signer » (ex « Retenir ») : visible uniquement si
                  le devis a été envoyé au client. Garde miroir backend 422. */}
              {isSent && q.status !== 'selected' && (
                <Button
                  variant="ghost"
                  size="sm"
                  leadingIcon={<Check className="h-3.5 w-3.5" />}
                  onClick={() => onAct(q.id, 'select')}
                >
                  Signer
                </Button>
              )}
              {q.status !== 'rejected' && (
                <Button
                  variant="ghost"
                  size="sm"
                  leadingIcon={<XIcon className="h-3.5 w-3.5" />}
                  onClick={() => onAct(q.id, 'reject')}
                >
                  Rejeter
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => onAct(q.id, 'delete')}>
                Supprimer
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Checklist
// ---------------------------------------------------------------------------

function ChecklistSection({
  opp,
  checklist,
  onReload,
}: {
  opp: OpportunityDetail;
  checklist: Checklist;
  onReload: () => Promise<void> | void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const [uploadingItemId, setUploadingItemId] = useState<string | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  async function handleFilePick(itemId: string, e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingItemId(itemId);
    try {
      await documentsApi.upload(token, file, {
        prospectId: opp.prospect.id,
        opportunityId: opp.id,
        category: 'other',
        checklistItemId: itemId,
      });
      toast({ tone: 'success', message: `« ${file.name} » ajouté à la checklist` });
      await onReload();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Upload impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    } finally {
      setUploadingItemId(null);
      const input = fileInputs.current[itemId];
      if (input) input.value = '';
    }
  }

  async function handleOpen(documentId: string) {
    try {
      const { url } = await documentsApi.downloadUrl(token, documentId);
      // Fenêtre ouverte AVANT l'await serait préférable pour éviter le
      // blocage pop-up - ici l'appel est déclenché sur un clic direct donc
      // les navigateurs autorisent `window.open` asynchrone qui suit. Si
      // bloqué par un bloqueur strict, l'utilisateur verra l'erreur.
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Ouverture impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  return (
    <Card>
      <div className="mb-3 flex items-start justify-between">
        <div>
          <CardTitle>Checklist documentaire</CardTitle>
          <CardDescription>
            {checklist.progress.fulfilled} / {checklist.progress.required} requis ·{' '}
            {checklist.progress.percent} %
          </CardDescription>
        </div>
      </div>
      <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            checklist.progress.percent === 100 ? 'bg-success-600' : 'bg-accent',
          )}
          style={{ width: `${checklist.progress.percent}%` }}
        />
      </div>
      <ul className="divide-y divide-gray-100">
        {checklist.items.map((item) => (
          <li key={item.id} className="flex items-center justify-between gap-3 py-2 text-sm">
            <div className="flex min-w-0 items-center gap-2">
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                  item.fulfilled ? 'bg-success-50 text-success-600' : 'bg-gray-100 text-gray-400',
                )}
              >
                {item.fulfilled ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                )}
              </span>
              <span
                className={cn(
                  'truncate',
                  item.fulfilled ? 'text-text-muted line-through' : 'text-brand',
                )}
              >
                {item.documentName}
              </span>
              {!item.isRequired && <Badge tone="neutral">Optionnel</Badge>}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {item.documentFileName && (
                <span className="max-w-[180px] truncate text-xs text-text-muted">
                  {item.documentFileName}
                </span>
              )}
              {item.fulfilled && item.documentId && (
                <Button
                  variant="ghost"
                  size="sm"
                  leadingIcon={<ExternalLink className="h-3 w-3" />}
                  onClick={() => void handleOpen(item.documentId!)}
                  aria-label={`Ouvrir « ${item.documentFileName ?? item.documentName} » dans un nouvel onglet`}
                >
                  Ouvrir
                </Button>
              )}
              {!item.fulfilled && (
                <>
                  <input
                    ref={(el) => {
                      fileInputs.current[item.id] = el;
                    }}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => handleFilePick(item.id, e)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    leadingIcon={<Upload className="h-3 w-3" />}
                    onClick={() => fileInputs.current[item.id]?.click()}
                    loading={uploadingItemId === item.id}
                  >
                    Uploader
                  </Button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
